export const DEFAULT_COLUMN_STATE = { order: [], hidden: [], pinnedField: null }

/**
 * Reconciles persisted column state against the live field list.
 * Stale fields are dropped, the order is de-duped, a pinned field that no longer exists
 * is cleared, and hidden choices are kept. New fields are displayed as visible at the end of the list.
 */
export function reconcileColumnState(storedState, currentFields) {
    if (!storedState) storedState = DEFAULT_COLUMN_STATE

    if (!currentFields?.length) {
        return {
            ...storedState,
            order: storedState.order ?? [],
            hidden: storedState.hidden ?? [],
            pinnedField: storedState.pinnedField ?? null,
        }
    }

    const current = new Set(currentFields)

    let { pinnedField } = storedState
    if (!current.has(pinnedField)) pinnedField = null

    const order = []
    for (const field of [...(storedState.order ?? []), ...currentFields]) {
        if (!current.has(field)) continue
        if (order.includes(field)) continue
        order.push(field)
    }

    const hidden = []
    for (const field of storedState.hidden ?? []) {
        if (!current.has(field)) continue
        if (field === pinnedField) continue
        if (hidden.includes(field)) continue
        hidden.push(field)
    }

    return { ...storedState, order, hidden, pinnedField }
}

export function promotePinned(columns, pinnedField) {
    const pinned = columns.find((column) => column.field === pinnedField)
    if (!pinned) return columns
    if (columns[0] === pinned) return columns
    return [pinned, ...columns.filter((column) => column !== pinned)]
}

export function resolveColumns(allColumns, columnState) {
    const { order, hidden, pinnedField } = columnState

    const visibleColumns = []
    for (const field of order) {
        if (hidden.includes(field)) continue
        const column = allColumns.find((candidate) => candidate.field === field)
        if (!column) continue
        visibleColumns.push(column)
    }

    return promotePinned(visibleColumns, pinnedField)
}

export function setOrder(columnState, orderedFields) {
    return { ...columnState, order: orderedFields }
}

export function reorderVisible(columnState, visibleFields) {
    const hidden = columnState.hidden ?? []
    const visibleSlots = (columnState.order ?? []).filter((field) => !hidden.includes(field)).length
    if (visibleSlots !== visibleFields.length) return columnState

    const remaining = [...visibleFields]

    const order = []
    for (const field of columnState.order ?? []) {
        if (hidden.includes(field)) order.push(field)
        else order.push(remaining.shift())
    }

    return { ...columnState, order }
}

export function pinField(columnState, field) {
    const isAlreadyPinned = columnState.pinnedField === field
    if (isAlreadyPinned) return { ...columnState, pinnedField: null }

    return {
        ...columnState,
        pinnedField: field,
        hidden: columnState.hidden.filter((hiddenField) => hiddenField !== field),
    }
}

export function toggleVisibility(state, field, min = 1) {
    if (state.hidden.includes(field)) return { ...state, hidden: state.hidden.filter((f) => f !== field) }
    // A pinned column cannot be hidden — it must be unpinned first
    if (field === state.pinnedField) return state
    if (visibleColumnCount(state) <= min) return state

    return {
        ...state,
        hidden: [...state.hidden, field],
    }
}

export function visibleColumnCount(state) {
    return state.order.length - state.hidden.length
}

export const TRAILING_COLUMN_COUNT = 2
